import { createAction, props } from '@ngrx/store';
import { AccessLogActor } from '../../models/access-log-actor';
import { AccessLogEntry } from '../../models/access-log-entry';
import { AccessLogDateRange, AccessLogTypeFilter } from '../../models/access-log-filters';

/**
 * Entering the page, or arriving on it as a different patient. Resets the filters to their defaults,
 * drops the previous patient's actor pick list, and drives both reads.
 *
 * One action rather than a `getAccessLogs` + `getAccessLogActors` pair at the call site: those are two
 * dispatches for a single conceptual transaction. It is also deliberately NOT `getAccessLogs`, which
 * must leave the filters alone — see `retryLoad` in the component.
 */
export const initAccessLogs = createAction('[Access Logs] Init Access Logs');

/** Full (re)load from page 1, preserving the current filters. Clears the loaded entries first. */
export const getAccessLogs = createAction('[Access Logs] Get Access Logs');

export const getAccessLogsSuccess = createAction(
  '[Access Logs] Get Access Logs Success',
  props<{
    entries: AccessLogEntry[];
    pageNumber: number;
    isAtEndOfData: boolean;
    totalRecordCount: number;
  }>()
);

export const getAccessLogsFailure = createAction('[Access Logs] Get Access Logs Failure', props<{ error: unknown }>());

/** Append the next page (infinite scroll). Increments `pageNumber`, keeps the loaded entries. */
export const nextPage = createAction('[Access Logs] Next Page');

/**
 * Re-request the page that just failed, without touching `pageNumber` or the loaded entries.
 *
 * Its own action rather than reusing `nextPage`, which *increments* `pageNumber` — retrying with it
 * would skip the page that failed. `getAccessLogs` is no good either: it clears the entries and
 * restarts at page 1, discarding what the patient has already scrolled through.
 */
export const retryPage = createAction('[Access Logs] Retry Page');

// Filter changes. Each resets paging to page 1 and clears the loaded entries, because every filter
// is applied server-side and the result set changes wholesale (PBI 56315 AC3).
export const setSearchTerm = createAction('[Access Logs] Set Search Term', props<{ searchTerm: string }>());

export const setDateRange = createAction('[Access Logs] Set Date Range', props<{ dateRange: AccessLogDateRange }>());

export const setTypeFilter = createAction(
  '[Access Logs] Set Type Filter',
  props<{ typeFilter: AccessLogTypeFilter }>()
);

export const setActorFilter = createAction('[Access Logs] Set Actor Filter', props<{ actorId: number | null }>());

/**
 * Resets the three dropdown filters to their defaults and deliberately **preserves** the typed search
 * term — the search field owns its own clear affordance, and the Clear control is only offered for
 * non-default dropdown selections. See the reducer, which is the authority on this.
 */
export const clearFilters = createAction('[Access Logs] Clear Filters');

/**
 * The filter changes that alter what the "Accessed by" pick list should offer — every filter except the
 * selection itself, which must not narrow its own options. Shared by both effects so a new filter is
 * wired into each from one place.
 */
export const actorListFilterChanges = [setSearchTerm, setDateRange, setTypeFilter, clearFilters] as const;

// Actor pick list. A separate read with its own error slot: a failed pick list must not make the
// list render its error state, and vice versa. Dispatched by `initAccessLogs` on entry, and directly
// by the component's retry when that read failed.
export const getAccessLogActors = createAction('[Access Logs] Get Access Log Actors');

export const getAccessLogActorsSuccess = createAction(
  '[Access Logs] Get Access Log Actors Success',
  props<{ actors: AccessLogActor[] }>()
);

export const getAccessLogActorsFailure = createAction(
  '[Access Logs] Get Access Log Actors Failure',
  props<{ error: unknown }>()
);
